import { Box } from '@mui/material'
import { MouseEvent } from 'react'
import { EyeIcon } from '@phosphor-icons/react'
import { BooleanButton } from 'ComponentLibrary/src/piazza/buttons/BooleanButton/BooleanButton'
import type { OrderListItem } from 'ClientPortal/src/pages/Orders/OrderList/OrderListPageContent.data'
import { validate as isUuid } from 'uuid'
import { useOrderObserverToggleData } from './OrderObserverToggleButton.data'
import { useTranslation } from 'react-i18next'

const OrderObserverToggleButton = ({ order, onSuccess }: { order: OrderListItem; onSuccess?: () => void }) => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderListPage.observer' })
    const { user, toggleObserver } = useOrderObserverToggleData({ order, onSuccess })

    const appraiserAssignmentUUIDs = (
        [order.appraiser_assignment?.main, order.appraiser_assignment?.secondary, order.appraiser_assignment?.tertiary].filter(
            Boolean
        ) as string[]
    ).filter(isUuid)

    if (!user) {
        return null
    }

    const observerUUIDs = order.observers ?? []
    const currentUserObserverIndex = observerUUIDs.indexOf(user.uuid)
    const isObserving = currentUserObserverIndex >= 0
    const isPlatformUser = !!user.is_platform_user
    const isAssignedCustomerUser = (order.assigned_customer_users ?? []).includes(user.id)

    const isAssignedAppraiser = appraiserAssignmentUUIDs.includes(user.uuid)
    const isProtectedObserver = isAssignedCustomerUser || isAssignedAppraiser
    const shouldDisableToggle = isPlatformUser || isProtectedObserver

    const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault()
        event.stopPropagation()
        toggleObserver({ isObserving, currentUserObserverIndex, currentUserUuid: user.uuid })
    }

    const tooltip = (() => {
        if (isProtectedObserver) {
            return t('protected')
        }
        if (isPlatformUser) {
            return t('platformUser')
        }
        if (isObserving) {
            return t('observing')
        }
        return t('notObserving')
    })()

    return (
        <Box>
            <BooleanButton
                selected={isObserving}
                onChange={handleToggle}
                icon={<EyeIcon />}
                disabled={shouldDisableToggle}
                tooltip={tooltip}
            />
        </Box>
    )
}

export default OrderObserverToggleButton
